import type { KeyboardEvent } from "react";
import { Trophy } from "lucide-react";

import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";
import { useCoverage } from "../context/CoverageContext";

export const BestNextWins = () => {
  const { bestNextWins, problemHubs, selectedHub, setSelectedHub } = useCoverage();

  const handleWinClick = (hubId: string) => {
    const hub = problemHubs.find((h) => h.id === hubId);
    if (hub) {
      setSelectedHub(hub);
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-hub-id="${hubId}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent, hubId: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleWinClick(hubId);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
      <div className="shrink-0 flex items-center gap-2 border-b border-border px-4 py-3">
        <p className="m-0 text-base font-normal leading-none tracking-tight text-foreground">Best next wins</p>
        {bestNextWins.length > 0 && (
          <Badge variant="muted" size="xs">
            {bestNextWins.length}
          </Badge>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-background">
        {bestNextWins.length === 0 ? (
          <div className="flex h-full min-h-[10rem] flex-col items-center justify-center gap-3 px-6 py-8 text-center">
            <Trophy className="size-5 text-muted-foreground/50" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">No wins to show</p>
              <p className="text-xs text-muted-foreground text-balance leading-relaxed">
                Best next wins highlight the hubs closest to being complete.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            {bestNextWins.map((win, i) => {
              const isSelected = selectedHub?.id === win.hubId;
              const actionText = win.gapType === "content"
                ? `add ${win.missingBucket}`
                : win.missingBucket.toLowerCase();
              const isLast = i === bestNextWins.length - 1;

              return (
                <div
                  key={`${win.hubId}-${win.missingBucket}-${i}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleWinClick(win.hubId)}
                  onKeyDown={(e) => handleKeyDown(e, win.hubId)}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 border-border/60 px-4 py-3.5 outline-none transition-colors",
                    "focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0",
                    !isLast && "border-b",
                    isSelected ? "bg-primary/10" : "hover:bg-muted/40"
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md",
                      isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                    )}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                      <path
                        d="M2 5h6M5 2l3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <div className="min-w-0 flex-1 pr-1 pt-0.5">
                    <p className="m-0 truncate text-sm font-normal leading-snug text-foreground">
                      {win.hubName}
                    </p>
                    <p className="m-0 mt-1.5 truncate text-2xs leading-snug text-muted-foreground">
                      {actionText}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
